import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { UserActions } from './user.actions';
import { UserApiService } from '@app/core/api/user';
import { LoggerService } from '@app/core/infrastructure';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly userApiService = inject(UserApiService);
  private readonly logger = inject(LoggerService).scope('UserEffects');

  loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadProfile),
      switchMap(() =>
        this.userApiService.getMe().pipe(
          map(profile => UserActions.loadProfileSuccess({ profile })),
          catchError(error => {
            this.logger.error('Load profile failed:', error);
            return of(UserActions.loadProfileFailure({ error: error.message }));
          })
        )
      )
    )
  );

  createOrSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.createOrSync),
      switchMap(() =>
        this.userApiService.createOrSyncMe().pipe(
          map(profile => UserActions.createOrSyncSuccess({ profile })),
          catchError(error => {
            this.logger.error('Create or sync failed:', error);
            return of(UserActions.createOrSyncFailure({ error: error.message }));
          })
        )
      )
    )
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfile),
      switchMap(({ data }) =>
        this.userApiService.updateMe(data).pipe(
          map(profile => UserActions.updateProfileSuccess({ profile })),
          catchError(error => {
            this.logger.error('Update profile failed:', error);
            return of(UserActions.updateProfileFailure({ error: error.message }));
          })
        )
      )
    )
  );

  deletePersonalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deletePersonalData),
      switchMap(() =>
        this.userApiService.deletePersonalData().pipe(
          map(() => UserActions.deletePersonalDataSuccess()),
          catchError(error => {
            this.logger.error('Delete personal data failed:', error);
            return of(UserActions.deletePersonalDataFailure({ error: error.message }));
          })
        )
      )
    )
  );

  deleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteAccount),
      switchMap(() =>
        this.userApiService.deleteMe().pipe(
          map(() => UserActions.deleteAccountSuccess()),
          catchError(error => {
            this.logger.error('Delete account failed:', error);
            return of(UserActions.deleteAccountFailure({ error: error.message }));
          })
        )
      )
    )
  );

  logSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserActions.loadProfileSuccess,
        UserActions.createOrSyncSuccess,
        UserActions.updateProfileSuccess
      ),
      tap(action => this.logger.debug('Success:', action.type))
    ),
    { dispatch: false }
  );
}
